import { formatNumber } from '../core/utils.js';

/**
 * Draws the Y-axis ticks/gridlines and returns a `yToPixel` mapper,
 * plus draws X-axis category labels and returns an `xForIndex` mapper.
 * Shared by line/bar/area/scatter renderers so they behave consistently.
 *
 * Supports optional chart.options.axisX / axisY styling:
 *   { gridColor, labelColor, labelFontFamily, labelFontSize, labelAngle, labelFormatter }
 */
export function drawCartesianAxes(chart, ctx, categories) {
  const { x, y, w, h } = chart._plot;
  const scale = chart._scaleY;
  const axisX = chart.options.axisX || {};
  const axisY = chart.options.axisY || {};

  const yToPixel = (value) => y + h - ((value - scale.min) / (scale.max - scale.min)) * h;

  const yLabelColor = axisY.labelColor || '#666';
  const yLabelFont = `${axisY.labelFontSize || 11}px ${axisY.labelFontFamily || 'Arial'}`;
  const yGridColor = axisY.gridColor || '#e0e0e0';

  ctx.strokeStyle = yGridColor;
  ctx.fillStyle = yLabelColor;
  ctx.font = yLabelFont;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';

  scale.ticks.forEach((t) => {
    const py = yToPixel(t);
    if (chart.options.showGrid) {
      ctx.beginPath();
      ctx.moveTo(x, py);
      ctx.lineTo(x + w, py);
      ctx.stroke();
    }
    ctx.fillText(formatNumber(t), x - 8, py);
  });

  // axis lines
  ctx.strokeStyle = '#999';
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x + w, y + h);
  ctx.stroke();

  const n = categories.length;
  const step = n > 1 ? w / (n - (chart.options.type === 'bar' ? 0 : 1)) : w;
  const xForIndex = (i) => (chart.options.type === 'bar' ? x + step * i + step / 2 : x + step * i);

  const xLabelColor = axisX.labelColor || '#666';
  const xLabelFont = `${axisX.labelFontSize || 11}px ${axisX.labelFontFamily || 'Arial'}`;
  const angle = axisX.labelAngle || 0; // degrees, negative = tilt up-left like CanvasJS
  const formatter = typeof axisX.labelFormatter === 'function' ? axisX.labelFormatter : (v) => String(v);

  ctx.fillStyle = xLabelColor;
  ctx.font = xLabelFont;
  ctx.textBaseline = 'top';

  const labelEvery = angle === 0 ? Math.ceil((n * 24) / Math.max(w, 1)) : 1;

  categories.forEach((label, i) => {
    if (labelEvery > 1 && i % labelEvery !== 0) return;
    const text = formatter({ label: String(label), value: label });
    const lx = xForIndex(i);
    const ly = y + h + 8;

    if (angle !== 0) {
      ctx.save();
      ctx.translate(lx, ly);
      ctx.rotate((angle * Math.PI) / 180);
      ctx.textAlign = angle < 0 ? 'right' : 'left';
      ctx.fillText(text, 0, 0);
      ctx.restore();
    } else {
      ctx.textAlign = 'center';
      ctx.fillText(text, lx, ly);
    }
  });

  return { yToPixel, xForIndex, step };
}

export function seriesCategories(series) {
  const first = series[0];
  if (!first) return [];
  return first.data.map((d, i) => (typeof d === 'object' ? d.x ?? d.label ?? i : i));
}

export function valueOf(d) {
  return typeof d === 'object' ? d.y ?? d.value : d;
}
